import { useEffect, useState } from 'react';
import { getConfig, getDbInfo } from '../api';
import type { ConfigResponse, DbInfoResponse } from '../types';

export default function Config() {
  const [config, setConfig] = useState<ConfigResponse | null>(null);
  const [db, setDb] = useState<DbInfoResponse | null>(null);

  useEffect(() => {
    getConfig().then(setConfig).catch(() => null);
    getDbInfo().then(setDb).catch(() => null);
  }, []);

  const renderValue = (_key: string, value: unknown) => {
    if (Array.isArray(value)) {
      return value.join(', ');
    }
    if (typeof value === 'number') {
      return value.toString();
    }
    return String(value);
  };

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-bold">Configuration</h2>

      {/* Strategy config */}
      <div className="bg-trader-card border border-trader-border rounded-lg p-4">
        <h3 className="text-sm font-semibold mb-3">Strategy Parameters</h3>
        {!config ? (
          <p className="text-trader-muted text-sm">Loading...</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2 text-sm">
            <div className="flex justify-between py-1 border-b border-trader-border/50">
              <span className="text-trader-muted">Max Exposure</span>
              <span className="font-mono">${config.max_exposure}</span>
            </div>
            <div className="flex justify-between py-1 border-b border-trader-border/50">
              <span className="text-trader-muted">Momentum Threshold</span>
              <span className="font-mono">{config.momentum_threshold}</span>
            </div>
            <div className="flex justify-between py-1 border-b border-trader-border/50">
              <span className="text-trader-muted">Reversion Z-Score</span>
              <span className="font-mono">{config.reversion_z_score}</span>
            </div>
            <div className="flex justify-between py-1 border-b border-trader-border/50">
              <span className="text-trader-muted">Kelly Fraction</span>
              <span className="font-mono">{config.kelly_fraction}</span>
            </div>
            <div className="flex justify-between py-1 border-b border-trader-border/50 sm:col-span-2">
              <span className="text-trader-muted">Market Categories</span>
              <span className="font-mono">{config.market_categories.join(', ')}</span>
            </div>
          </div>
        )}
      </div>

      {/* Maker config */}
      {config?.maker && (
        <div className="bg-trader-card border border-trader-border rounded-lg p-4">
          <h3 className="text-sm font-semibold mb-3">Maker Configuration</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2 text-sm">
            {Object.entries(config.maker).map(([key, val]) => (
              <div key={key} className="flex justify-between py-1 border-b border-trader-border/50">
                <span className="text-trader-muted">{key.replace(/_/g, ' ')}</span>
                <span className="font-mono">{renderValue(key, val)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* DB schema */}
      <div className="bg-trader-card border border-trader-border rounded-lg p-4">
        <h3 className="text-sm font-semibold mb-3">Database Schema</h3>
        {!db ? (
          <p className="text-trader-muted text-sm">Loading...</p>
        ) : (
          <div className="space-y-4">
            {Object.entries(db.tables).map(([name, info]) => (
              <div key={name} className="border border-trader-border/50 rounded p-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-mono text-trader-info">{name}</span>
                  <span className="text-xs text-trader-muted">{info.count} rows</span>
                </div>
                <pre className="text-xs text-trader-muted bg-black/30 rounded p-2 overflow-x-auto whitespace-pre-wrap">
                  {info.schema}
                </pre>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
